/*
 * hp-analytics — the theme's single GA4 dispatcher.
 *
 * ── ONE ENTRY POINT ────────────────────────────────────────────────────────
 * Everything in the theme calls `hpTrack(name, params)`. Nothing calls `gtag`
 * directly, and nothing anywhere else decides whether analytics is configured,
 * consented to, or loaded. That is the only reason the send path can be swapped
 * (gtag to GTM, or a second destination added) without touching a call site.
 *
 * ── TWO WAYS TO FIRE AN EVENT, AND THE DECLARATIVE ONE IS PREFERRED ────────
 *
 *   <button data-hp-track="select_swatch"
 *           data-hp-track-params='{"item_id":"123","color":"Moss"}'>
 *
 *   hpTrack('select_swatch', { item_id: '123', color: 'Moss' });
 *
 * The attribute form needs no JavaScript per area — one delegated listener on
 * the document catches every element that ever exists, including markup
 * injected later. That matters here specifically: the predictive-search panel
 * replaces its results on every keystroke and Section Rendering swaps whole
 * grids and the cart, so a listener bound per element at load would cover a
 * shrinking fraction of the page. Same reasoning as hp-card-swatch.js.
 *
 * ── THE QUEUES ARE THE INTERESTING PART ────────────────────────────────────
 * There are two, doing different jobs, and both exist because an event can be
 * fired before the thing that would send it is ready:
 *
 *   the STUB queue    filled by the inline stub in snippets/hp-analytics.liquid
 *                     before this deferred file parses. Drained once, below.
 *   the CONSENT queue filled while consent is still unknown, drained if and
 *                     when marketing consent arrives.
 *
 * Neither is a nicety. Without the first, any event fired from section markup
 * during parse is lost; without the second, gating consent would mean dropping
 * every event that happened before the visitor answered the banner.
 */
(function () {
  const CFG = window.hpAnalyticsConfig || {};
  const DEBUG = CFG.debug === true;
  const ENABLED = CFG.enabled === true;
  const CONSENT_GATE = CFG.consentGate === true;

  const TRACK_ATTR = '[data-hp-track]';

  /* Held while consent is unknown. Unused when the gate is off. */
  const consentQueue = [];
  let consentResolved = !CONSENT_GATE;

  /*
   * ── THE BADGE HAS TO SURVIVE A DARK CONSOLE ──────────────────────────────
   * A devtools console has no theme we can query, so these values are fixed and
   * have to work on BOTH grounds. The first version was Dark Brown 100 on
   * white text, which measured 1.06:1 against Chrome's dark console -- a brown
   * chip on a near-black field, effectively invisible, which is exactly what it
   * looked like.
   *
   * Inverting it to Cream 50 is 12.99:1 on that dark ground, but only 1.13:1 on
   * a light one, so a straight swap just moves the problem. The BORDER is what
   * makes one pair work everywhere: on a dark console the cream fill carries it,
   * and on a light console the fill disappears into the page while the dark
   * border and the dark label still define the chip. The label itself is
   * 13.81:1 on the cream either way.
   *
   * The name colours are a genuine compromise rather than a free win: no single
   * value is strong on white AND on near-black, so both are tuned to favour the
   * dark ground while staying legible on light (sent 4.43 dark / 3.33 light,
   * skipped 4.63 / 3.19 -- against the previous 2.88 and 3.48 on dark).
   *
   * The REASON grey is deliberately the quietest thing on the line; it is
   * secondary to the event name in every case.
   */
  const BADGE =
    'color:#321e1e;background:#f4f1e0;border:1px solid #321e1e;' +
    'padding:1px 6px;border-radius:3px;font-weight:700';
  const NAME_SENT = 'color:#22a06b;font-weight:600';
  const NAME_SKIPPED = 'color:#d97706;font-weight:600';
  const REASON = 'color:#9e9e9e';

  function log() {
    if (!DEBUG) return;
    const args = Array.prototype.slice.call(arguments);
    /* eslint-disable-next-line no-console */
    console.log.apply(console, ['%cGA4', BADGE].concat(args));
  }

  /*
   * Event logging is separated from diagnostic logging because it is read
   * differently: while an area's events are being built, the console IS the
   * test surface, and what matters is scanning a column of event names and
   * spotting the one that did not fire. So the name is the loudest thing on the
   * line, the outcome is colour-coded, and the parameters are logged as a live
   * OBJECT rather than stringified -- the browser renders it expandable, which
   * is what makes a nested `items` array readable at all.
   *
   * A dropped event is logged as loudly as a sent one, with the reason. Silence
   * would make "no ID configured" and "this click never fired" look identical,
   * and those are the two things most worth telling apart during a build.
   */
  function logEvent(sent, name, params, reason) {
    if (!DEBUG) return;
    /* eslint-disable-next-line no-console */
    console.log(
      '%cGA4%c ' + name + '%c' + (reason ? '  ' + reason : ''),
      BADGE,
      sent ? NAME_SENT : NAME_SKIPPED,
      REASON,
      params || {}
    );
  }

  /*
   * The ONLY place gtag is called. `enabled` is the Liquid-side answer to "is
   * there an ID and are we outside the theme editor"; the typeof check is the
   * runtime one, because gtag.js is async and a very early event can beat it.
   *
   * A dropped event is logged rather than swallowed. Debug mode works with no
   * measurement ID at all, which is what makes it possible to build and QA a
   * new area's events before the property exists.
   */
  function send(name, params) {
    const payload = params || {};
    if (!ENABLED) {
      logEvent(false, name, payload, CFG.measurementId ? '(not sent: disabled)' : '(not sent: no measurement ID)');
      return;
    }
    if (typeof window.gtag !== 'function') {
      logEvent(false, name, payload, '(not sent: gtag not loaded yet)');
      return;
    }
    window.gtag('event', name, payload);
    logEvent(true, name, payload);
  }

  function track(name, params) {
    if (!name || typeof name !== 'string') {
      log('ignored, no event name:', name, params);
      return;
    }
    if (!consentResolved) {
      consentQueue.push([name, params]);
      logEvent(false, name, params, '(queued: awaiting consent)');
      return;
    }
    send(name, params);
  }

  /* ── Consent ───────────────────────────────────────────────────────────────
   * Off by default (the theme setting). When on, events are held until
   * Shopify's Customer Privacy API says marketing/analytics processing is
   * allowed, and dropped outright if the visitor refuses.
   *
   * This is the simple form: hold, then flush. GA4's own Consent Mode
   * (`gtag('consent', 'default'|'update', ...)`) is the more rigorous option —
   * it keeps cookieless pings and modelled conversions rather than losing the
   * pre-consent events entirely — and is the thing to reach for if the data
   * team asks why gated traffic under-reports. Deliberately not built until
   * someone needs it, because it is a data-modelling decision, not a code one.
   */
  function marketingAllowed() {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return null;
    try {
      return cp.analyticsProcessingAllowed() === true;
    } catch (e) {
      return null;
    }
  }

  function resolveConsent() {
    if (consentResolved) return;
    const allowed = marketingAllowed();
    if (allowed === null) return; /* API not ready — stay queued */

    consentResolved = true;
    if (!allowed) {
      log('consent refused, dropping ' + consentQueue.length + ' queued event(s)');
      consentQueue.length = 0;
      return;
    }
    log('consent granted, flushing ' + consentQueue.length + ' queued event(s)');
    while (consentQueue.length) {
      const entry = consentQueue.shift();
      send(entry[0], entry[1]);
    }
  }

  if (CONSENT_GATE) {
    document.addEventListener('visitorConsentCollected', resolveConsent);
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (error) {
            /*
             * The API failed to load, so consent can never be established and
             * every event would queue forever. Failing OPEN here would send
             * data the gate exists to withhold, so it fails CLOSED and says so.
             */
            log('consent API failed to load; events will not be sent', error);
            consentQueue.length = 0;
            return;
          }
          resolveConsent();
        }
      );
    }
  }

  /* ── Declarative binding ──────────────────────────────────────────────────
   * `data-hp-track-params` is authored by Liquid (see hp-analytics-attrs), so a
   * parse failure is a bug in a snippet rather than bad user input — but it is
   * caught anyway, because one malformed attribute must not stop the listener
   * working for every other element on the page.
   */
  function paramsFor(el) {
    const raw = el.getAttribute('data-hp-track-params');
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      log('bad data-hp-track-params on', el, raw);
      return {};
    }
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const el = target.closest(TRACK_ATTR);
    if (!el) return;
    track(el.getAttribute('data-hp-track'), paramsFor(el));
  });

  /* ── Declarative VIEW binding ─────────────────────────────────────────────
   * The counterpart to the click binding above, for events that fire because
   * something was RENDERED rather than clicked — `view_item`, `view_item_list`.
   * Markup comes from snippets/hp-analytics-view.liquid; the parameters are read
   * from the same escaped-JSON attribute the click form uses, so there is one
   * escaping rule in the theme rather than two.
   *
   * ── WHY A MUTATION OBSERVER AND NOT JUST A LOAD-TIME SCAN ────────────────
   * Because a load-time scan would cover a shrinking fraction of this theme. A
   * collection filter replaces the whole results region, `loadMore` appends a
   * page into the grid, the cart re-renders on every quantity change, and
   * predictive search rewrites its panel on each keystroke. Every one of those
   * is a fresh impression the server has already described; the observer is what
   * notices it arrived.
   *
   * Elements are marked as they fire, so re-scanning is free and idempotent. An
   * element that a swap REPLACED is a new element with no mark, and fires again
   * — which is correct: a re-filtered grid is a new list impression, not the
   * same one seen twice.
   */
  const VIEW_ATTR = '[data-hp-view]:not([data-hp-view-fired])';

  function scanViews() {
    const nodes = document.querySelectorAll(VIEW_ATTR);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      /*
       * Marked BEFORE the event is sent, not after. `track` can run arbitrary
       * work (a consent flush, gtag itself) and a throw part-way through must
       * not leave the element unmarked for the next scan to fire again.
       */
      el.setAttribute('data-hp-view-fired', '');
      const raw = el.getAttribute('data-hp-view-params');
      let params = {};
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') params = parsed;
        } catch (e) {
          log('bad data-hp-view-params on', el, raw);
        }
      }
      track(el.getAttribute('data-hp-view'), params);
    }
  }

  /*
   * Coalesced to one scan per frame. A Section Rendering swap produces a burst
   * of mutations for what is a single logical change, and a full-document query
   * per mutation record would run it dozens of times for no extra events.
   * Querying the whole document rather than walking each added subtree is both
   * simpler and safer — a payload nested inside an added node is found either
   * way, and there is no path where a node is added without a mutation.
   */
  let viewScanScheduled = false;

  /*
   * setTimeout, NOT requestAnimationFrame. rAF was the reflex — it is the
   * cheaper callback and a view event has no reason to beat a paint — and it is
   * a mechanism that can silently never run: it does not fire in a backgrounded
   * tab, so a cart or grid updated in a tab the visitor had switched away from
   * would hold its impression indefinitely, and it does not fire in the browser
   * pane at all, which would make this path untestable in the one place the
   * theme is verified. Same reasoning CLAUDE.md records for the image-fade
   * deadline: a guarantee conditional on something that can not-run is not a
   * guarantee. A timer always fires.
   */
  function scheduleViewScan() {
    if (viewScanScheduled) return;
    viewScanScheduled = true;
    setTimeout(function () {
      viewScanScheduled = false;
      scanViews();
    }, 0);
  }

  new MutationObserver(function (records) {
    for (let i = 0; i < records.length; i++) {
      if (records[i].addedNodes.length) {
        scheduleViewScan();
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  /*
   * This file is deferred, so parsing is finished and every server-rendered
   * payload is already in the document. Scanned synchronously rather than on
   * the next frame so a page-view event is not held behind a paint.
   */
  scanViews();

  /* ── Take over from the stub ──────────────────────────────────────────────
   * The stub's queue is drained through `track`, not `send`, so anything fired
   * before this file parsed still respects the consent gate.
   */
  const stub = window.hpAnalytics;
  const queued = (stub && Array.isArray(stub.queue) && stub.queue) || [];

  window.hpAnalytics = {
    track: track,
    /* Exposed for debugging and for tests; not part of the call-site API. */
    isEnabled: function () { return ENABLED; },
    isDebug: function () { return DEBUG; },
    pendingConsentCount: function () { return consentQueue.length; }
  };
  window.hpTrack = track;

  log('ready', {
    enabled: ENABLED,
    consentGate: CONSENT_GATE,
    measurementId: CFG.measurementId || '(none)',
    drained: queued.length
  });

  queued.forEach(function (entry) {
    track(entry[0], entry[1]);
  });
})();
